import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios';

const initialState = {
    customerProfile : null,
    profiles : [],
    isLoading : false,
    error : '',
    profileFound : false,
    // The shop whose full customer list is currently cached in `profiles`. Used to fetch
    // the (large) list at most once per shop per session — see getCustomerProfiles' condition.
    loadedShop : null
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API_URL = `${BACKEND_URL}/api/customerprofile/`;

//Get single Website
export const getCustomerProfileByContact = createAsyncThunk(
  "customerProfile/getOneByContact",
  async(contact, thunkAPI) => {
    try {
        const response = await axios.post(API_URL + '/bycontact', {contact})
        return response.data
    } catch (error) {
      const message = (
        error.response && error.response.data 
        && error.response.data.message) 
        || error.message || error.toString();
        console.log(message)
        return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getCustomerProfileBySmile = createAsyncThunk(
  "customerProfile/getOneBySmile",
  async({smileName, includeOrders, shop, page, shipDateId}, thunkAPI) => {
    try {
        const body = {smileName, includeOrders, shop};
        if (includeOrders && page !== undefined) {
          body.page = page;
        }
        // Pass shipDateId to backend as-is: 'all', 'none', or number
        // Backend will handle the filtering logic
        if (shipDateId !== undefined && shipDateId !== null) {
          body.shipDateId = shipDateId;
        }
        const response = await axios.post(API_URL + 'bysmile', body)
        console.log(response)
        return response.data
    } catch (error) {
      const message = (
        error.response && error.response.data 
        && error.response.data.message) 
        || error.message || error.toString();
        console.log(message)
        return thunkAPI.rejectWithValue(message)
    }
  }
)

// The whole customer table. It is large and was being re-downloaded on every board mount,
// socket event and create-modal open (I24). Now cached per shop for the session: the
// `condition` skips the request when this shop's list is already loaded. Callers that need
// fresh data (e.g. ShipDatePayment after a payment updates advanceAmount, the customer
// management page) pass { shop, force: true } to bypass the cache.
// Arg accepts a plain shop string (cached) or { shop, force }.
export const getCustomerProfiles = createAsyncThunk( //Fix Shop
  "customerProfile/getAll",
  async(arg, thunkAPI) => {
    const shop = arg && typeof arg === 'object' ? arg.shop : arg;
    try {
        const response = await axios.post(API_URL, { shop });
        return response.data
    } catch (error) {
      const message = (
        error.response && error.response.data
        && error.response.data.message)
        || error.message || error.toString();
        console.log(message)
        return thunkAPI.rejectWithValue(message)
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg && typeof arg === 'object' && arg.force) return true;
      const shop = arg && typeof arg === 'object' ? arg.shop : arg;
      const s = getState().customerProfile;
      // Already have this shop's list → skip the download (RTK dispatches no action, so the
      // cache in `profiles` is untouched).
      if (s.loadedShop === shop && Array.isArray(s.profiles)) return false;
      return true;
    },
  }
)


const customerProfileSlice = createSlice({
  name: "customerProfile",
  initialState,
  reducers: {
    addProfileToState: (state, action) => {
      state.profiles.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerProfiles.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getCustomerProfiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profiles = action.payload
        const arg = action.meta.arg;
        state.loadedShop = arg && typeof arg === 'object' ? arg.shop : arg;
      })
      .addCase(getCustomerProfiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload
        state.profiles = null
      })
      .addCase(getCustomerProfileByContact.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getCustomerProfileByContact.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customerProfile = action.payload
        state.profileFound = true
      })
      .addCase(getCustomerProfileByContact.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload
        state.profileFound = false
        state.customerProfile = null
      })
      .addCase(getCustomerProfileBySmile.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getCustomerProfileBySmile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customerProfile = action.payload
        state.profileFound = true
      })
      .addCase(getCustomerProfileBySmile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload
        state.profileFound = false
        state.customerProfile = null
        })
  }
});

export const selectCustomerProfile = (state) => state.customerProfile.customerProfile
export const selectCustomerProfiles = (state) => state.customerProfile.profiles
export const selectCustomerProfileFound = (state) => state.customerProfile.profileFound
export const {addProfileToState} = customerProfileSlice.actions

export default customerProfileSlice.reducer